import { destination } from '@turf/turf';
import {
  getClosestPointOnMultiPolygon,
  getFlightPlanBorderColor,
  getTargetColor,
  isOrientedTarget,
} from '@utils/map/map.utils';
import { getCrossOpacity } from '@utils/map/target.utils';
import {
  getContingencyIcon,
  getExpiredDroneIconName,
  getTargetIconName,
  getTargetIdentification,
  getTargetRotation,
  isAircraft,
  isSure,
  isVCA,
} from '@utils/target.utils';
import { toLonLatArray } from '@utils/validation/coordinates.utils';
import { Feature, Position } from 'geojson';

import { Target, TargetStatusEnum } from '@/types/c2/c2.types';
import { DroneNotification } from '@/types/data/data.types';
import { FeatureTypeEnum } from '@/types/map.types';
import { IdentificationEnum } from '@/types/sensor/identification.types';
import { TargetLocation } from '@/types/sensor/sensor.types';
import { AbstractFlightPlan } from '@/types/utm/flightPlan.types';

export function toTargetIconFeatures(targets: Target<TargetLocation>[], organization: string | null): Feature[] {
  return targets.map((target) => {
    const shouldBeOriented = isOrientedTarget(target, organization) && isSure(target);

    return {
      type: 'Feature',
      id: `${FeatureTypeEnum.TARGET_ICON}-${target.id}`,
      properties: {
        id: target.id,
        featureType: FeatureTypeEnum.TARGET_ICON,
        value: target,
        identification: getTargetIdentification(target, organization),
        icon: getTargetIconName(target, organization),
        contingencyIcon: getContingencyIcon(target, organization),
        ...(shouldBeOriented ? { heading: getTargetRotation(target, organization) } : {}),
        selectOptionLabel: target.displayId ?? target.id,
      },
      geometry: {
        type: 'Point',
        coordinates: toLonLatArray(target.lastPosition!.location!.position, false),
      },
    } as Feature;
  });
}

export function toExpiredDroneIconFeature(notification: DroneNotification): Feature {
  return {
    type: 'Feature',
    id: `${FeatureTypeEnum.EXPIRED_DRONE_ICON}-${notification.identifier}`,
    properties: {
      id: notification.identifier,
      featureType: FeatureTypeEnum.EXPIRED_DRONE_ICON,
      value: notification,
      identification: IdentificationEnum.DRONE,
      icon: getExpiredDroneIconName(notification.classification, notification.reliability),
    },
    geometry: {
      type: 'Point',
      coordinates: toLonLatArray(notification.position, false),
    },
  } as Feature;
}

export function toTargetCrossFeature(targetPosition: Position, date: Date | null): Feature[] {
  if (date === null) {
    return [];
  }
  const timeSinceSelection = (Date.now() - new Date(date).getTime()) / 1000;

  return [
    {
      type: 'Feature',
      properties: {
        featureType: FeatureTypeEnum.TARGET_CROSS,
        opacity: getCrossOpacity(timeSinceSelection),
      },
      geometry: {
        type: 'MultiLineString',
        coordinates: [
          [targetPosition, [targetPosition[0], 90]],
          [targetPosition, [targetPosition[0], -90]],
          [targetPosition, [180, targetPosition[1]]],
          [targetPosition, [-180, targetPosition[1]]],
        ],
      },
    },
  ] as Feature[];
}

export function toTargetLinkFeatures(
  groupedTargets: [Target<TargetLocation>, Target<TargetLocation>][],
  organization: string | null,
): Feature[] {
  return groupedTargets.map((targetGroup) => {
    return {
      type: 'Feature',
      id: `${FeatureTypeEnum.TARGET_LINK}-${targetGroup[1].id}`,
      properties: {
        featureType: FeatureTypeEnum.TARGET_LINK,
        color: getTargetColor(targetGroup[1], organization),
      },
      geometry: {
        type: 'LineString',
        coordinates: [
          toLonLatArray(targetGroup[0].lastPosition!.location!.position, false),
          toLonLatArray(targetGroup[1].lastPosition!.location!.position, false),
        ],
      },
    } as Feature;
  });
}

export function toTargetTrajectoryFeatures(targets: Target<TargetLocation>[], organization: string | null): Feature[] {
  return targets.flatMap((target) => {
    const identification = getTargetIdentification(target, organization);
    const trajectoryOutlineFeature = {
      type: 'Feature',
      id: `${FeatureTypeEnum.TARGET_TRAJECTORY}-outline-${target.id}`,
      properties: {
        id: target.id,
        featureType: FeatureTypeEnum.TARGET_TRAJECTORY,
        identification,
        color: 'black',
        width: 5,
        opacity: target.status === TargetStatusEnum.ONGOING ? 1 : 0.5,
      },
      geometry: {
        type: 'LineString',
        coordinates: target.trajectory.map((plot) => toLonLatArray(plot.location?.position)).filter((pos) => !!pos),
      },
    };
    const trajectoryFeature = {
      ...trajectoryOutlineFeature,
      id: `${FeatureTypeEnum.TARGET_TRAJECTORY}-${target.id}`,
      properties: {
        id: target.id,
        featureType: FeatureTypeEnum.TARGET_TRAJECTORY,
        identification,
        color: getTargetColor(target, organization),
        width: 2,
        opacity: target.status === TargetStatusEnum.ONGOING ? 1 : 0.5,
      },
    };

    return [trajectoryOutlineFeature, trajectoryFeature] as Feature[];
  });
}

export function toTargetSpeedFeatures(
  targets: Target<TargetLocation>[],
  speedVectorLength: number,
  organization: string | null,
): Feature[] {
  return targets
    .filter((target) => isAircraft(target, organization) || isVCA(target, organization))
    .flatMap((target) => {
      const speed = target.lastPosition.location?.speed;
      const bearing = target.lastPosition.location?.heading;
      const position = target.lastPosition.location?.position;
      if (!speed || bearing === undefined || bearing === null || isNaN(bearing) || !position) {
        return [];
      }
      const distance = speed * speedVectorLength;
      const destinationCoordinates = destination(toLonLatArray(position, false)!, distance / 1000, bearing, {
        units: 'kilometers',
      });

      return {
        type: 'Feature',
        id: `${FeatureTypeEnum.TARGET_SPEED}-${target.id}`,
        properties: {
          id: target.id,
          featureType: FeatureTypeEnum.TARGET_SPEED,
          color: 'black',
          width: 2,
          opacity: target.status === TargetStatusEnum.ONGOING ? 1 : 0.5,
        },
        geometry: {
          type: 'LineString',
          coordinates: [toLonLatArray(position, false), destinationCoordinates.geometry.coordinates],
        },
      } as Feature;
    });
}

export function toExpiredDroneTrajectoryFeatures(notification: DroneNotification): Feature[] {
  const trajectoryOutlineFeature = {
    type: 'Feature',
    id: `${FeatureTypeEnum.TARGET_TRAJECTORY}-${notification.id}`,
    properties: {
      id: notification.id,
      featureType: FeatureTypeEnum.TARGET_TRAJECTORY,
      identification: IdentificationEnum.DRONE,
      color: 'white',
      width: 5,
    },
    geometry: {
      type: 'LineString',
      coordinates: notification.trajectory.map((plot) => toLonLatArray(plot)).filter((pos) => !!pos),
    },
  };
  const trajectoryFeature = {
    ...trajectoryOutlineFeature,
    properties: {
      id: notification.id,
      featureType: FeatureTypeEnum.TARGET_TRAJECTORY,
      identification: IdentificationEnum.DRONE,
      color: 'black',
      width: 2,
    },
  };

  return [trajectoryOutlineFeature, trajectoryFeature] as Feature[];
}

export function toTargetFlightPlansLinkFeatures(dronesWithFlightPlans: {
  [key: string]: { drone: Target<TargetLocation>; flightPlans: AbstractFlightPlan[] };
}): Feature[] {
  return Object.values(dronesWithFlightPlans).flatMap(({ drone, flightPlans }) => {
    return flightPlans.map((flightPlan) => {
      const droneLocation = toLonLatArray(drone.lastPosition.location!.position, false)!;
      return {
        type: 'Feature',
        id: `${FeatureTypeEnum.TARGET_FLIGHT_PLAN_LINK}-${drone.id}`,
        properties: {
          featureType: FeatureTypeEnum.TARGET_FLIGHT_PLAN_LINK,
          color: getFlightPlanBorderColor(flightPlan),
        },
        geometry: {
          type: 'LineString',
          coordinates: [
            toLonLatArray(drone.lastPosition.location!.position, false),
            getClosestPointOnMultiPolygon(droneLocation, flightPlan.polygons),
          ],
        },
      } as Feature;
    });
  });
}
